import { resolveMethodKeyFromName } from './withdrawalWallets';

const pickId = (obj, ...keys) => {
  for (const key of keys) {
    const val = obj?.[key];
    if (val !== undefined && val !== null && String(val).trim() !== '') {
      return String(val).trim();
    }
  }
  return '';
};

/** Logged-in partner identity from localStorage (login userData). */
export const getLoggedInPartnerIdentity = () => {
  let userData = null;
  try {
    userData = JSON.parse(localStorage.getItem('userData') || 'null');
  } catch {
    userData = null;
  }

  const userId = pickId(
    userData,
    'userId',
    'UserId',
    'affiliateUserId',
    'AffiliateUserId',
    'id',
    'Id'
  );
  const affiliateCode = pickId(
    userData,
    'affiliateCode',
    'AffiliateCode',
    'partnerCode',
    'PartnerCode',
    'userCode',
    'UserCode'
  ) || String(localStorage.getItem('affiliateCode') || '').trim();
  const email = pickId(userData, 'email', 'Email', 'emailAddress', 'EmailAddress')
    || String(localStorage.getItem('userEmail') || '').trim();

  return { userId, affiliateCode, email };
};

export const extractPartnerPaymentDetailsList = (response) => {
  if (!response?.success) return [];
  const payload = response.data;
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (Array.isArray(payload?.partnerPaymentDetails)) return payload.partnerPaymentDetails.filter(Boolean);
  if (Array.isArray(payload?.result)) return payload.result.filter(Boolean);
  if (Array.isArray(payload?.data)) return payload.data.filter(Boolean);
  if (Array.isArray(payload?.items)) return payload.items.filter(Boolean);
  if (Array.isArray(payload?.records)) return payload.records.filter(Boolean);
  if (payload && typeof payload === 'object') return [payload];
  return [];
};

const isBankAccountType = (accountType) =>
  (accountType || '').toString().trim().toLowerCase() === 'bank';

export const mapPartnerPaymentDetail = (item, index = 0) => {
  const accountType = item?.accountType ?? '';
  const accountNo = item?.accountNo ?? '';
  const accountAddress =
    item?.accountAddress ?? accountNo ?? item?.accountNumber ?? item?.walletAddress ?? '';
  const accountCode =
    item?.accountCode ?? item?.accountIBAN ?? item?.swiftCode ?? item?.iban ?? '';
  const accountDescription =
    item?.accountDescription ??
    item?.accountBank ??
    item?.bankName ??
    item?.subPaymentMethodName ??
    item?.subPaymentMethodTitle ??
    '';

  const isBank = isBankAccountType(accountType) || inferMethodKey(item) === 'bank';
  const paymentDetailId =
    item?.userPaymentDetailId ??
    item?.partnerPaymentDetailId ??
    item?.affiliateWalletId ??
    item?.paymentDetailId ??
    item?.id ??
    null;

  return {
    id: String(paymentDetailId ?? index),
    userPaymentDetailId: paymentDetailId,
    partnerPaymentDetailId: paymentDetailId,
    walletId: item?.walletId ?? null,
    walletTitle:
      item?.walletName ??
      item?.walletTitle ??
      item?.paymentMethodName ??
      (accountDescription || 'Withdrawal Account'),
    methodKey: isBank ? 'bank' : inferMethodKey(item),
    subPaymentMethodId: item?.subPaymentMethodId ?? '',
    subPaymentMethodTitle:
      item?.subPaymentMethodName ??
      item?.subPaymentMethodTitle ??
      item?.networkName ??
      ((!isBank ? accountCode : '') || accountDescription || ''),
    accountTitle: item?.accountTitle ?? item?.accountHolder ?? item?.accountName ?? '',
    accountNumber: isBank ? accountNo || accountAddress : '',
    bankName: isBank ? accountDescription : '',
    iban: isBank ? accountCode : '',
    swiftCode: isBank ? accountCode : '',
    cryptoAddress: isBank ? item?.cryptoAddress ?? '' : accountAddress,
    accountCode,
    accountType,
    remarks: item?.remarks ?? item?.Remarks ?? '',
    raw: item,
  };
};

const inferMethodKey = (item) => {
  const accountType = (item?.accountType || '').toString().toLowerCase();
  if (accountType === 'bank') return 'bank';
  if (accountType === 'crypto') return 'crypto';
  const name = [
    item?.walletName,
    item?.walletTitle,
    item?.accountDescription,
    item?.accountBank,
    item?.title,
  ]
    .filter(Boolean)
    .join(' ');
  return resolveMethodKeyFromName(name);
};

export const maskAccountNumber = (value = '') => {
  const trimmed = value.toString().trim();
  if (!trimmed) return '—';
  if (trimmed.length <= 4) return trimmed;
  return `**** ${trimmed.slice(-4)}`;
};

export const shortenAddress = (value = '') => {
  const trimmed = value.toString().trim();
  if (!trimmed) return '—';
  if (trimmed.length <= 12) return trimmed;
  return `${trimmed.slice(0, 6)}...${trimmed.slice(-4)}`;
};

export const normalizeAccountCode = (value = '') => value.toString().trim().toLowerCase();

export const accountMatchesNetwork = (account, networkValue = '') => {
  const networkCode = normalizeAccountCode(networkValue);
  if (!networkCode) return false;
  const accountCode = normalizeAccountCode(
    account?.accountCode ?? account?.raw?.accountCode ?? account?.iban ?? ''
  );
  return accountCode === networkCode;
};

export const buildPartnerPaymentDetailPayload = ({
  selectedWallet,
  selectedSubPaymentMethod,
  form,
  userPaymentDetailId = null,
}) => {
  const isBank = selectedWallet?.methodKey === 'bank';
  const accountNumber = form.accountNumber?.trim() || '';
  const cryptoAddress = form.cryptoAddress?.trim() || '';

  const payload = {
    walletId: Number(selectedWallet?.walletId) || 0,
    accountTitle: form.accountTitle?.trim() || '',
    accountAddress: isBank ? accountNumber : cryptoAddress,
    accountCode: isBank
      ? form.iban?.trim() || ''
      : selectedSubPaymentMethod?.value ||
        selectedSubPaymentMethod?.title ||
        selectedSubPaymentMethod?.code ||
        '',
    accountDescription: isBank
      ? form.bankName?.trim() || selectedWallet?.title || ''
      : selectedSubPaymentMethod?.title || selectedWallet?.title || '',
  };

  if (userPaymentDetailId != null && userPaymentDetailId !== '') {
    return {
      ...payload,
      userPaymentDetailId: Number(userPaymentDetailId) || 0,
    };
  }

  return payload;
};
